import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, MapPin, Clock, ChevronRight, X, Phone, Home } from 'lucide-react'
import {
  createJob,
  checkPostcodeAvailability,
  getApiErrorMessage,
  getNearbyPlumberPreviews,
  reportFunnelStep,
  getJob,
  requestEmailLogin,
  verifyEmailLogin,
  getMe,
} from '../lib/api'
import { UK_PHONE_RE, UK_POSTCODE_RE, formatPostcode, normalizePhone } from '../lib/utils'
import { JOB_CATEGORIES, buildIssueDescription } from '../lib/jobCategories'
import { loadSession, saveSession, clearSession } from '../hooks/useFormSession'
import { logStatsigEvent } from '../lib/statsig'
import { SUPPORT_PHONE_DISPLAY, SHOW_SUPPORT_PHONE } from '../lib/contact'
import { TextUsLink } from './TextUsLink'
import { useAuth } from '../hooks/useAuth'


// ─── Coming soon guard ─────────────────────────────────────────────────────────

const COMING_SOON = import.meta.env['VITE_COMING_SOON'] === 'true'

function ComingSoon() {
  return (
    <div className="text-center py-4">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-gold-50 rounded-full mb-4">
        <Clock className="h-6 w-6 text-gold-500" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Launching soon</h3>
      <p className="text-sm text-gray-500 leading-relaxed">
        FixDelivered is almost ready. We're finalising our network of qualified plumbers and will be live very soon.
      </p>
    </div>
  )
}

// ─── Availability result ───────────────────────────────────────────────────────

interface AvailabilityResult {
  available: boolean
  plumberCount: number
}

// ─── Main component ────────────────────────────────────────────────────────────

export function EmergencyForm() {
  if (COMING_SOON) return <ComingSoon />

  const navigate = useNavigate()
  const saved = loadSession()

  const [step, setStep]           = useState<number>(saved?.step ?? 0)
  const [animKey, setAnimKey]     = useState(0)

  // Field values
  const [postcode, setPostcode]               = useState(saved?.postcode ?? '')
  const [category, setCategory]               = useState<string>(saved?.category ?? '')
  const [subSelections, setSubSelections]     = useState<string[]>(saved?.subSelections ?? [])
  const [followUpValues, setFollowUpValues]   = useState<Record<string, string>>(saved?.followUpValues ?? {})
  const [phone, setPhone]                     = useState(saved?.phone ?? '')
  const [email, setEmail]                     = useState(saved?.email ?? '')
  const [name, setName]                       = useState(saved?.name ?? '')
  const [address, setAddress]                 = useState(saved?.address ?? '')
  const [addressLine2, setAddressLine2]       = useState(saved?.addressLine2 ?? '')
  const [additionalInfo, setAdditionalInfo]   = useState(saved?.additionalInfo ?? '')


  // Async state
  const [availability, setAvailability]               = useState<AvailabilityResult | null>(
    saved?.plumberCount ? { available: true, plumberCount: saved.plumberCount } : null,
  )
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  const [resumableJobId, setResumableJobId]           = useState<string | null>(null)
  const [resumeDismissed, setResumeDismissed]         = useState(false)
  const [submitting, setSubmitting]                   = useState(false)
  const [error, setError]                             = useState<string | null>(null)
  const [modalOpen, setModalOpen]                     = useState((saved?.step ?? 0) > 0 && !saved?.jobId)
  const [modalReady, setModalReady]                   = useState((saved?.step ?? 0) > 0 && !saved?.jobId)
  const [modalOrigin, setModalOrigin]                 = useState('50% 50%')
  const [cardWidth, setCardWidth]                     = useState<number | undefined>(undefined)
  const [confirmClose, setConfirmClose]               = useState(false)

  // Field errors
  const [postcodeError, setPostcodeError]   = useState<string | null>(null)
  const [categoryError, setCategoryError]   = useState<string | null>(null)
  const [phoneError, setPhoneError]         = useState<string | null>(null)
  const [emailError, setEmailError]         = useState<string | null>(null)
  const [addressError, setAddressError]     = useState<string | null>(null)

  // Email-login state — shown inline on step 2 if the email already has an account
  const [awaitingOtp, setAwaitingOtp]       = useState(false)
  const [otpCode, setOtpCode]               = useState('')
  const [otpError, setOtpError]             = useState<string | null>(null)
  const [otpSubmitting, setOtpSubmitting]   = useState(false)
  const [phoneSubmitting, setPhoneSubmitting] = useState(false)

  const { signInWithCustomToken, user } = useAuth()

  // Prefill contact details from the signed-in customer's account (only fills
  // empty fields, so anything already typed / restored from session wins).
  useEffect(() => {
    if (!user) return
    let cancelled = false
    getMe()
      .then(me => {
        if (cancelled) return
        if (me.email) setEmail(prev => prev || me.email!)
        if (me.phone) setPhone(prev => prev || me.phone!)
      })
      .catch(() => { /* not fatal — user can type them */ })
    return () => { cancelled = true }
  }, [user])

  useEffect(() => {
    if (awaitingOtp) otpInputRefs.current[0]?.focus()
  }, [awaitingOtp])

  const postcodeRef       = useRef<HTMLInputElement>(null)
  const phoneRef          = useRef<HTMLInputElement>(null)
  const addressRef        = useRef<HTMLInputElement>(null)
  const addressLine2Ref   = useRef<HTMLInputElement>(null)
  const additionalInfoRef = useRef<HTMLTextAreaElement>(null)
  const formContainerRef  = useRef<HTMLDivElement>(null)
  const modalPanelRef     = useRef<HTMLDivElement>(null)
  const otpInputRefs      = useRef<Array<HTMLInputElement | null>>([])

  const postcodeNormalized = postcode.trim().toUpperCase()
  const selectedCategory   = JOB_CATEGORIES.find(c => c.id === category)

  // ── Can the user advance from step 1? ────────────────────────────────────────
  const canContinue = useMemo(() => {
    if (!category) return false
    if (category === 'other') return (followUpValues['other']?.trim().length ?? 0) >= 20
    const cat = JOB_CATEGORIES.find(c => c.id === category)
    if (!cat?.subOptions?.length) return true          // no sub-question
    if (subSelections.length === 0) return false       // must pick at least one
    // Check required follow-ups
    for (const id of subSelections) {
      const opt = cat.subOptions.find(o => o.id === id)
      if (opt?.followUp?.required && !followUpValues[id]?.trim()) return false
    }
    return true
  }, [category, subSelections, followUpValues])


  const { data: plumberPreviews, isLoading: loadingPlumberPreviews } = useQuery({
    queryKey: ['nearby-plumber-previews', postcodeNormalized],
    queryFn: () => getNearbyPlumberPreviews(postcodeNormalized),
    // Prefetch as soon as the postcode is valid (step 0), before the popup opens,
    // so the preview avatars are already loading by the time step 1 renders.
    enabled: step <= 1 && UK_POSTCODE_RE.test(postcodeNormalized),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!saved?.jobId) return
    let cancelled = false
    getJob(saved.jobId)
      .then(job => {
        if (cancelled) return
        if (job.status === 'CANCELLED' || job.status === 'FAILED' || job.status === 'COMPLETED') {
          clearSession()
        } else if (job.status === 'AWAITING_PAYMENT') {
          // Abandoned checkout — payment hasn't been taken and plumbers are only
          // contacted after payment, so nothing is in flight. Let them start over.
          // (PENDING is NOT abandonable: it's set on payment success just before
          // calling starts, and the session holds the paid job's jobSecret.)
          clearSession()
        } else {
          // Paid and in progress. Don't hard-redirect (it looks like a glitch);
          // offer a way back instead. Keep the session — it holds the jobSecret,
          // an anonymous customer's only access to the job page.
          setResumableJobId(saved.jobId ?? null)
        }
      })
      .catch(() => { clearSession() })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const focusField = () => {
      if (step === 0) postcodeRef.current?.focus()
      else if (step === 2) phoneRef.current?.focus()
      else if (step === 3) addressRef.current?.focus()
      else if (step === 4) additionalInfoRef.current?.focus()
    }
    const t1 = window.setTimeout(focusField, 80)
    const t2 = window.setTimeout(focusField, 260)
    const t3 = window.setTimeout(focusField, 520)
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); window.clearTimeout(t3) }
  }, [step])

  useEffect(() => {
    if (step !== 0) return undefined
    const onFirstPointer = () => {
      if (document.activeElement !== postcodeRef.current) postcodeRef.current?.focus()
      window.removeEventListener('pointerdown', onFirstPointer)
      window.removeEventListener('touchstart', onFirstPointer)
    }
    window.addEventListener('pointerdown', onFirstPointer, { passive: true })
    window.addEventListener('touchstart', onFirstPointer, { passive: true })
    return () => {
      window.removeEventListener('pointerdown', onFirstPointer)
      window.removeEventListener('touchstart', onFirstPointer)
    }
  }, [step])

  // Measure card position → compute modal transform-origin so it grows from the card.
  // Runs both on fresh open (modalReady=false → also triggers animation) and on
  // session-restore (modalReady=true → just sets cardWidth for correct sizing).
  useLayoutEffect(() => {
    if (!modalOpen) return
    const panel = modalPanelRef.current
    const card  = formContainerRef.current?.parentElement
    if (!card) { if (!modalReady) setModalReady(true); return }

    const cardRect = card.getBoundingClientRect()
    setCardWidth(Math.min(cardRect.width, 720))

    if (!modalReady) {
      if (panel) {
        const panelRect = panel.getBoundingClientRect()
        const cx = cardRect.left + cardRect.width  / 2
        const cy = cardRect.top  + cardRect.height / 2
        setModalOrigin(`${cx - panelRect.left}px ${cy - panelRect.top}px`)
      }
      setModalReady(true)
    }
  }, [modalOpen, modalReady])

  const advance = useCallback((newStep: number) => {
    setStep(newStep)
    setAnimKey(k => k + 1)
  }, [])

  // ── Step 0: Postcode ─────────────────────────────────────────────────────────

  async function submitPostcode() {
    const trimmed = postcode.trim().toUpperCase()
    if (!UK_POSTCODE_RE.test(trimmed)) {
      setPostcodeError('Please enter a valid UK postcode')
      return
    }
    setPostcodeError(null)
    setCheckingAvailability(true)
    setError(null)
    try {
      const result = await checkPostcodeAvailability(trimmed)
      setAvailability(result)
      logStatsigEvent('booking_postcode_submitted', trimmed, {
        available: String(result.available),
        plumberCount: String(result.plumberCount),
      })
      // NEVER tell the customer there are no matches. plumberCount 0 simply
      // shows no count anywhere; the server has already escalated to support.
      saveSession({ ...loadSession(), postcode: trimmed, step: 1, jobRef: result.jobRef, plumberCount: result.plumberCount })
      if (result.jobRef) void reportFunnelStep({ jobRef: result.jobRef, step: 'postcode', postcode: trimmed, plumberCount: result.plumberCount }).catch(() => {})
      advance(1)
      setModalReady(false)
      setConfirmClose(false)
      setModalOpen(true)
    } catch (err) {
      // Shown right under the postcode input (step 0 is outside the modal,
      // which doesn't open until this succeeds) - the general `error` state
      // only renders inside the modal (steps 2-5), so it would never be seen
      // here even though it was being set. getApiErrorMessage already
      // surfaces the backend's friendly text (e.g. "We couldn't find that
      // postcode - please double check it and try again.") for a real
      // postcode that just isn't recognised (PostcodeNotFoundError).
      setPostcodeError(getApiErrorMessage(err, 'Could not check availability. Please try again.'))
    } finally {
      setCheckingAvailability(false)
    }
  }

  // ── Step 1: Category + sub-questions ─────────────────────────────────────────

  function toggleSubOption(id: string) {
    const cat = JOB_CATEGORIES.find(c => c.id === category)
    let newSubs: string[]
    if (cat?.multiSelect) {
      newSubs = subSelections.includes(id) ? subSelections.filter(s => s !== id) : [...subSelections, id]
      setSubSelections(newSubs)
    } else {
      newSubs = [id]
      setSubSelections(newSubs)
      setFollowUpValues({})
    }
    setCategoryError(null)
    const _jobRef = loadSession()?.jobRef
    if (_jobRef) {
      const subLabels = newSubs.map(sid => cat?.subOptions?.find(o => o.id === sid)?.label ?? sid)
      void reportFunnelStep({ jobRef: _jobRef, step: 'category', category: cat?.label ?? category, subSelections: subLabels }).catch(() => {})
    }
  }

  function setFollowUp(optId: string, value: string) {
    setFollowUpValues(prev => ({ ...prev, [optId]: value }))
  }

  function submitCategory() {
    if (!canContinue) {
      if (!category) setCategoryError('Please select the type of issue')
      else if (category === 'other') setCategoryError('Please describe the problem in at least 20 characters')
      else setCategoryError('Please answer the question above before continuing')
      return
    }
    setCategoryError(null)
    logStatsigEvent('booking_category_submitted', category, { subSelections: String(subSelections.length) })
    saveSession({ ...loadSession(), category, subSelections, followUpValues, step: 2 })
    advance(2)
  }

  // ── Step 2: Phone ────────────────────────────────────────────────────────────

  async function submitPhone() {
    const normalized = normalizePhone(phone)
    const trimmedEmail = email.trim().toLowerCase()
    let hasError = false
    if (!UK_PHONE_RE.test(normalized)) {
      setPhoneError('Please enter a valid UK mobile number (e.g. 07700 900123)')
      hasError = true
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address')
      hasError = true
    }
    if (hasError) return
    setPhoneError(null)
    setEmailError(null)
    setPhone(normalized)
    setEmail(trimmedEmail)
    const session = loadSession()
    saveSession({ ...session, phone: normalized, email: trimmedEmail, name: name.trim() || undefined, step: 3 })
    const jobRefNow = session?.jobRef
    const jobIdNow  = session?.jobId
    if (jobRefNow) void reportFunnelStep({ jobRef: jobRefNow, step: 'phone', phone: normalized, email: trimmedEmail }).catch(() => {})
    logStatsigEvent('booking_contact_submitted', undefined, { signedIn: String(!!user) })

    // Passwordless account creation / silent sign-in.
    // New email → account created + signed in; existing email → OTP sent.
    // Any auth-service failure must NOT block the booking funnel.
    setPhoneSubmitting(true)
    try {
      // Already signed in — don't create an account or send an OTP, just continue.
      if (user) {
        advance(3)
        return
      }
      const result = await requestEmailLogin({
        email: trimmedEmail,
        phone: normalized,
        ...(jobIdNow ? { jobId: jobIdNow } : {}),
      })
      if (result.customToken) {
        try { await signInWithCustomToken(result.customToken) } catch { /* silent */ }
        advance(3)
      } else if (result.requiresOtp) {
        setAwaitingOtp(true)
        setOtpCode('')
        setOtpError(null)
      } else {
        advance(3)
      }
    } catch {
      advance(3)
    } finally {
      setPhoneSubmitting(false)
    }
  }

  async function submitOtp() {
    if (!/^\d{4}$/.test(otpCode)) {
      setOtpError('Enter the 4-digit code from your inbox')
      return
    }
    setOtpError(null)
    setOtpSubmitting(true)
    const session = loadSession()
    try {
      const { customToken } = await verifyEmailLogin({
        email: email.trim().toLowerCase(),
        code: otpCode,
        phone: normalizePhone(phone),
        ...(session?.jobId ? { jobId: session.jobId } : {}),
      })
      try { await signInWithCustomToken(customToken) } catch { /* silent */ }
      logStatsigEvent('booking_otp_verified')
      setAwaitingOtp(false)
      advance(3)
    } catch (err) {
      setOtpError(getApiErrorMessage(err) || 'That code didn\'t work — try again')
    } finally {
      setOtpSubmitting(false)
    }
  }

  async function resendOtp() {
    setOtpError(null)
    try {
      await requestEmailLogin({ email: email.trim().toLowerCase(), phone: normalizePhone(phone) })
    } catch {
      setOtpError('Couldn\'t resend — try again in a moment')
    }
  }

  // ── Step 3: Address ───────────────────────────────────────────────────────────

  function submitAddress() {
    const line1 = address.trim()
    if (line1.length < 2) {
      setAddressError('Please enter your street address')
      return
    }
    setAddressError(null)
    const line2 = addressLine2.trim()
    const combined = line2 ? `${line1}, ${line2}` : line1
    logStatsigEvent('booking_address_submitted')
    saveSession({ ...loadSession(), address: combined, addressLine2: line2, step: 4 })
    const _jobRef3 = loadSession()?.jobRef
    if (_jobRef3) void reportFunnelStep({ jobRef: _jobRef3, step: 'address', address: combined }).catch(() => {})
    setAddress(combined)
    advance(4)
  }

  // ── Step 4: Add Extra Info + submit ──────────────────────────────────────────

  function submitAdditionalInfo(skip = false) {
    const info = skip ? '' : additionalInfo
    saveSession({ ...loadSession(), additionalInfo: info, step: 5 })
    void submitFinal(false, undefined, info)
  }

  // ── Submit ────────────────────────────────────────────────────────────────────

  async function submitFinal(skipExtra = false, addressOverride?: string, infoOverride?: string) {
    const info = skipExtra ? '' : (infoOverride ?? additionalInfo)
    const finalAddress = addressOverride ?? address.trim()
    setSubmitting(true)
    setError(null)
    try {
      const issueDescription = buildIssueDescription({
        categoryId: category,
        subSelections,
        followUpValues,
        otherText: followUpValues['other'] ?? '',
        additionalInfo: info,
      })
      const { jobId, jobSecret, status } = await createJob({
        phone: phone.trim(),
        email: email.trim().toLowerCase() || undefined,
        name: name.trim() || undefined,
        postcode: postcode.trim().toUpperCase(),
        issueDescription,
        category,
        address: finalAddress,
        jobRef: loadSession()?.jobRef,
      })
      saveSession({ ...loadSession(), additionalInfo: info, jobId, jobSecret, step: 5 })
      // paywallVariant lets Statsig segment conversion by which variant the
      // job was created under (issue #40) - derived from status rather than a
      // new field, since createJob() already tells us: only the upfront-
      // payment path (paywall on, or an admin-forced call) ever returns
      // AWAITING_PAYMENT here, everything else means the free path applied.
      // source: 'form' pairs with ChatPanel.tsx's matching tag on the same
      // event, so chat- and form-originated bookings share one funnel instead
      // of chat being invisible to it.
      logStatsigEvent('booking_job_created', jobId, {
        category,
        source: 'form',
        paywallVariant: status === 'AWAITING_PAYMENT' ? 'paywall_on' : 'paywall_off',
      })
      navigate(`/job/${jobId}`)
    } catch (err) {
      logStatsigEvent('booking_job_create_failed', undefined, { message: getApiErrorMessage(err, 'unknown'), source: 'form' })
      setError(getApiErrorMessage(err, 'Could not submit your request. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  const MODAL_STEPS = 4  // steps 1-4 inside modal
  const modalProgress = step > 0 ? (step / MODAL_STEPS) * 100 : 0

  // Active follow-up inputs for selected sub-options
  const activeFollowUps = (selectedCategory?.subOptions ?? []).filter(
    opt => opt.followUp && subSelections.includes(opt.id),
  )

  return (
    <>
      {/* ── Step 0: Postcode (inline) ─────────────────────────────────────── */}
      <div ref={formContainerRef} className={`transition-all duration-500 ${modalOpen ? 'opacity-20 scale-[0.97] pointer-events-none select-none' : ''}`}>
        {resumableJobId && !resumeDismissed && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-gold-100 bg-gold-50 px-4 py-3">
            <Clock className="h-5 w-5 text-gold-600 flex-shrink-0" />
            <p className="text-sm text-gray-700 flex-1">You have a job in progress.</p>
            <button
              type="button"
              onClick={() => navigate(`/job/${resumableJobId}`)}
              className="text-sm font-semibold text-gold-700 hover:text-gold-600 transition-colors whitespace-nowrap"
            >
              View status
            </button>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setResumeDismissed(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Where are you?</h3>
            <p className="text-sm text-gray-400 mb-4">We'll check which plumbers are available near you right now.</p>
          </div>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <MapPin className="h-5 w-5" />
            </div>
            <input
              ref={postcodeRef}
              id="postcode-input"
              type="text"
              inputMode="text"
              enterKeyHint="next"
              value={postcode}
              onChange={e => { setPostcode(formatPostcode(e.target.value)); setPostcodeError(null) }}
              onInput={e => { e.currentTarget.value = formatPostcode(e.currentTarget.value) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void submitPostcode() } }}
              placeholder="SW1A 1AA"
              autoComplete="postal-code"
              className="w-full rounded-xl border-2 border-gray-200 pl-11 pr-4 py-5 text-gray-900 placeholder-gray-300 focus:outline-none focus:border-emergency-500 focus:ring-4 focus:ring-emergency-500/10 transition-all text-2xl font-bold tracking-[0.25em] uppercase text-center bg-gray-50 focus:bg-white"
            />
          </div>
          {postcodeError && <p className="text-sm text-red-600">{postcodeError}</p>}
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          {/* Redundant with the sticky mobile call bar, so only shown from sm: up. */}
          {SHOW_SUPPORT_PHONE && (
            <TextUsLink className="hidden sm:inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0">
              <Phone className="h-3.5 w-3.5" />
              Need help? Text us · {SUPPORT_PHONE_DISPLAY}
            </TextUsLink>
          )}
          <button
            type="button"
            disabled={checkingAvailability}
            onClick={() => void submitPostcode()}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emergency-500 hover:bg-emergency-600 disabled:opacity-60 text-white font-bold px-6 py-3 rounded-xl transition-colors text-base whitespace-nowrap ml-auto"
          >
            {checkingAvailability ? 'Checking…' : <>Get help now <ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>
      </div>

      {/* ── Steps 1-4: Modal ─────────────────────────────────────────────── */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex flex-col sm:justify-center sm:items-center sm:p-4">
          {/* Backdrop — click does nothing, only X closes */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" />

          {/* Panel */}
          <div
            ref={modalPanelRef}
            style={{
              transformOrigin: modalOrigin,
              ...(window.innerWidth >= 640 && cardWidth ? { width: `${cardWidth}px`, maxWidth: `${cardWidth}px` } : {}),
            }}
            className={`relative flex flex-col w-full h-full sm:h-auto bg-white sm:rounded-2xl shadow-2xl overflow-hidden ${modalReady ? 'animate-slide-up' : 'opacity-0'}`}
          >

            {/* Modal header bar */}
            <div className="flex items-center justify-between px-9 pt-6 pb-2">
              <span className="text-xs text-gray-400 font-mono tracking-wide" data-clarity-mask="true">{loadSession()?.jobRef ?? ''}</span>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  const hasProgress = step >= 2 || (step === 1 && !!category)
                  if (hasProgress) { setConfirmClose(true) } else { clearSession(); setStep(0); setModalOpen(false) }
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-0.5 bg-gray-100 mx-5">
              <div
                className="h-full bg-emergency-500 rounded-full transition-all duration-500"
                style={{ width: `${modalProgress}%` }}
              />
            </div>

            {/* Step content */}
            <div className="px-9 pt-7 pb-8 flex-1 overflow-y-auto sm:max-h-[75vh] flex flex-col sm:block justify-center">
              <div key={animKey} className="step-in">

                {/* ── Step 1: Category ──────────────────────────────────── */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      {loadingPlumberPreviews && (
                        <div className="flex -space-x-3 shrink-0" aria-hidden>
                          {[0, 1, 2].map(i => (
                            <div key={i} className="h-9 w-9 rounded-full bg-gray-100 animate-pulse ring-2 ring-white" />
                          ))}
                        </div>
                      )}
                      {!loadingPlumberPreviews && plumberPreviews && plumberPreviews.length > 0 && (
                        <div className="flex -space-x-3 shrink-0">
                          {plumberPreviews.map(p => (
                            <img
                              key={p.id}
                              src={p.imageUrl}
                              alt={p.companyName}
                              width={36}
                              height={36}
                              loading="eager"
                              decoding="async"
                              className="h-9 w-9 rounded-full border-2 border-white object-cover bg-white shadow-sm ring-1 ring-gray-200"
                              title={p.companyName}
                            />
                          ))}
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 leading-tight">What's the issue?</h3>
                        {availability?.plumberCount ? (
                          <p className="text-sm text-emergency-600 font-medium">
                            {availability.plumberCount} emergency plumber{availability.plumberCount !== 1 ? 's' : ''} available in {postcode.trim().toUpperCase()}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">Select the type of problem.</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {JOB_CATEGORIES.map(cat => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setCategory(cat.id)
                            setSubSelections([])
                            setFollowUpValues({})
                            setCategoryError(null)
                            const _jobRef = loadSession()?.jobRef
                            if (_jobRef) void reportFunnelStep({ jobRef: _jobRef, step: 'category', category: cat.label }).catch(() => {})
                          }}
                          className={`flex items-center gap-2.5 px-3 py-3 rounded-xl border-2 text-left transition-all text-sm ${
                            category === cat.id
                              ? 'border-emergency-500 bg-emergency-50 text-emergency-700 font-semibold'
                              : 'border-gray-200 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <span className={`text-lg leading-none flex-shrink-0 transition-all ${category === cat.id ? 'grayscale-0' : 'grayscale'}`}>{cat.emoji}</span>
                          <span className="font-medium leading-snug">{cat.label}</span>
                        </button>
                      ))}
                    </div>

                    {(category === 'other' || (selectedCategory?.subOptions && selectedCategory.subOptions.length > 0)) && (
                      <hr className="border-gray-200" />
                    )}

                    {category === 'other' && (
                      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                        <div className="flex items-baseline justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-700">Describe the problem</label>
                          <span className={`text-xs tabular-nums ${(followUpValues['other']?.length ?? 0) >= 20 ? 'text-gray-400' : 'text-red-500'}`}>
                            {followUpValues['other']?.length ?? 0}/20 min
                          </span>
                        </div>
                        <textarea
                          rows={3}
                          value={followUpValues['other'] ?? ''}
                          onChange={e => setFollowUp('other', e.target.value)}
                          placeholder="e.g. Water meter leak, underground pipe noise, bathroom refit…"
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors resize-none"
                        />
                      </div>
                    )}

                    {selectedCategory?.subOptions && selectedCategory.subOptions.length > 0 && (
                      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
                        <p className="text-sm font-semibold text-gray-700">{selectedCategory.subQuestion}</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedCategory.subOptions.map(opt => {
                            const selected = subSelections.includes(opt.id)
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => toggleSubOption(opt.id)}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-all ${
                                  selected
                                    ? 'border-emergency-500 bg-emergency-50 text-emergency-700 font-semibold'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                }`}
                              >
                                <span className={`leading-none transition-all ${selected ? 'grayscale-0' : 'grayscale'}`}>{opt.emoji}</span>
                                <span>{opt.label}</span>
                              </button>
                            )
                          })}
                        </div>
                        {activeFollowUps.length > 0 && (
                          <div className="space-y-2 pt-1">
                            {activeFollowUps.map(opt => (
                              <div key={opt.id}>
                                <label className="block text-xs font-medium text-gray-600 mb-1">{opt.followUp!.label}</label>
                                <input
                                  type="text"
                                  value={followUpValues[opt.id] ?? ''}
                                  onChange={e => setFollowUp(opt.id, e.target.value)}
                                  placeholder={opt.followUp!.placeholder}
                                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {categoryError && <p className="text-sm text-red-600 mt-1">{categoryError}</p>}
                  </div>
                )}

                {/* ── Step 2: Phone ─────────────────────────────────────── */}
                {step === 2 && !awaitingOtp && (
                  <div className="space-y-5">
                    <div>
                      <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emergency-50 mb-3">
                        <Phone className="h-5 w-5 text-emergency-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">Your contact details</h3>
                      <p className="text-sm text-gray-400 mb-1">So we can reach you about your booking.</p>
                      {availability?.plumberCount ? (
                        <p className="text-sm text-emergency-600 font-medium mb-4">
                          ✓ {availability.plumberCount} emergency plumber{availability.plumberCount !== 1 ? 's' : ''} available in {postcode.trim().toUpperCase()}
                        </p>
                      ) : <div className="mb-4" />}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Your name <span className="text-gray-400 font-normal">(optional)</span></label>
                          <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); phoneRef.current?.focus() } }}
                            placeholder="Jane Smith"
                            autoComplete="name"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors text-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Phone number</label>
                          <input
                            ref={phoneRef}
                            type="tel"
                            value={phone}
                            onChange={e => { setPhone(e.target.value); setPhoneError(null) }}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('emergency-email')?.focus() } }}
                            placeholder="07700 900000"
                            autoComplete="tel"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors text-lg"
                          />
                          {phoneError && <p className="text-sm text-red-600 mt-1">{phoneError}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Email address</label>
                          <input
                            id="emergency-email"
                            type="email"
                            value={email}
                            onChange={e => { setEmail(e.target.value); setEmailError(null) }}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void submitPhone() } }}
                            placeholder="you@example.com"
                            autoComplete="email"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors text-lg"
                          />
                          {emailError && <p className="text-sm text-red-600 mt-1">{emailError}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 2b: Existing-account OTP (shown when requiresOtp) ─ */}
                {step === 2 && awaitingOtp && (
                  <div className="space-y-5">
                    <div>
                      <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emergency-50 mb-3">
                        <Phone className="h-5 w-5 text-emergency-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">Check your inbox</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        You already have an account — we sent a 4-digit code to <span className="font-medium text-gray-700">{email}</span>.
                      </p>
                      <label className="block text-xs font-medium text-gray-500 mb-2">4-digit code</label>
                      <div className="flex items-center justify-center gap-3">
                        {[0, 1, 2, 3].map(i => (
                          <input
                            key={i}
                            ref={el => { otpInputRefs.current[i] = el }}
                            type="text"
                            inputMode="numeric"
                            autoComplete={i === 0 ? 'one-time-code' : 'off'}
                            maxLength={1}
                            value={otpCode[i] ?? ''}
                            aria-label={`Digit ${i + 1}`}
                            onChange={e => {
                              const digit = e.target.value.replace(/\D/g, '').slice(-1)
                              const next = (otpCode.padEnd(4, ' ').slice(0, i) + digit + otpCode.padEnd(4, ' ').slice(i + 1)).replace(/\s+$/, '')
                              setOtpCode(next.slice(0, 4))
                              setOtpError(null)
                              if (digit && i < 3) otpInputRefs.current[i + 1]?.focus()
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Backspace') {
                                if (otpCode[i]) {
                                  setOtpCode(otpCode.slice(0, i) + otpCode.slice(i + 1))
                                } else if (i > 0) {
                                  e.preventDefault()
                                  otpInputRefs.current[i - 1]?.focus()
                                  setOtpCode(otpCode.slice(0, i - 1) + otpCode.slice(i))
                                }
                              } else if (e.key === 'ArrowLeft' && i > 0) {
                                e.preventDefault()
                                otpInputRefs.current[i - 1]?.focus()
                              } else if (e.key === 'ArrowRight' && i < 3) {
                                e.preventDefault()
                                otpInputRefs.current[i + 1]?.focus()
                              } else if (e.key === 'Enter' && otpCode.length === 4) {
                                e.preventDefault()
                                void submitOtp()
                              }
                            }}
                            onPaste={e => {
                              const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4)
                              if (!pasted) return
                              e.preventDefault()
                              setOtpCode(pasted)
                              setOtpError(null)
                              const focusIdx = Math.min(pasted.length, 3)
                              otpInputRefs.current[focusIdx]?.focus()
                            }}
                            className="w-14 h-16 rounded-xl border border-gray-200 bg-white text-center text-2xl font-semibold text-gray-900 caret-emergency-500 focus:outline-none focus:ring-2 focus:ring-emergency-500/25 focus:border-emergency-500 transition-colors"
                          />
                        ))}
                      </div>
                      {otpError && <p className="text-sm text-red-600 mt-3 text-center">{otpError}</p>}
                      <div className="flex items-center justify-between mt-3">
                        <button
                          type="button"
                          onClick={() => { setAwaitingOtp(false); setOtpCode(''); setOtpError(null) }}
                          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          Use a different email
                        </button>
                        <button
                          type="button"
                          onClick={() => void resendOtp()}
                          className="text-xs text-emergency-600 hover:text-emergency-700 font-medium transition-colors"
                        >
                          Resend code
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 3: Address ───────────────────────────────────── */}
                {step === 3 && (
                  <div className="space-y-5">
                    <div>
                      <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emergency-50 mb-3">
                        <Home className="h-5 w-5 text-emergency-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">Your address <span className="text-base font-normal text-gray-400">({postcode.trim().toUpperCase()})</span></h3>
                      <p className="text-sm text-gray-400 mb-1">So your plumber knows exactly where to come.</p>
                      {availability?.plumberCount ? (
                        <p className="text-sm text-emergency-600 font-medium mb-4">
                          ✓ {availability.plumberCount} emergency plumber{availability.plumberCount !== 1 ? 's' : ''} available in {postcode.trim().toUpperCase()}
                        </p>
                      ) : <div className="mb-4" />}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Address Line 1</label>
                          <input
                            ref={addressRef}
                            type="text"
                            value={address}
                            onChange={e => { setAddress(e.target.value); setAddressError(null) }}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addressLine2Ref.current?.focus() } }}
                            placeholder="e.g. Flat 2, 12 Church Street"
                            autoComplete="address-line1"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors text-base"
                          />
                          {addressError && <p className="text-sm text-red-600 mt-1">{addressError}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Address Line 2 <span className="text-gray-400 font-normal">(optional)</span></label>
                          <input
                            ref={addressLine2Ref}
                            type="text"
                            value={addressLine2}
                            onChange={e => setAddressLine2(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitAddress() } }}
                            placeholder="e.g. Battersea"
                            autoComplete="address-line2"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors text-base"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 4: Add Extra Info + submit ───────────────────── */}
                {step === 4 && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">Add Extra Info</h3>
                      <p className="text-sm text-gray-400 mb-4">Optional — any details that'll help your plumber prepare.</p>
                      <textarea
                        ref={additionalInfoRef}
                        value={additionalInfo}
                        onChange={e => setAdditionalInfo(e.target.value)}
                        rows={4}
                        placeholder="e.g. Stopcock is under the kitchen sink. Access via side gate. Call when outside."
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emergency-500/20 focus:border-emergency-500 transition-colors resize-none text-base"
                      />
                    </div>
                  </div>
                )}

              </div>

              {/* Error */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Modal footer: nav buttons */}
            <div className="px-9 py-6 border-t border-gray-100 flex items-center justify-between gap-4">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => { setError(null); advance(step - 1) }}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ← Back
                </button>
              ) : <span />}
              {step === 1 && (
                <button
                  type="button"
                  onClick={() => submitCategory()}
                  className="inline-flex items-center gap-2 bg-emergency-500 hover:bg-emergency-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-base"
                >
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              )}
              {step === 2 && !awaitingOtp && (
                <button
                  type="button"
                  disabled={phoneSubmitting}
                  onClick={() => void submitPhone()}
                  className="inline-flex items-center gap-2 bg-emergency-500 hover:bg-emergency-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-base disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {phoneSubmitting ? 'Checking…' : <>Continue <ChevronRight className="h-4 w-4" /></>}
                </button>
              )}
              {step === 2 && awaitingOtp && (
                <button
                  type="button"
                  disabled={otpSubmitting || otpCode.length !== 4}
                  onClick={() => void submitOtp()}
                  className="inline-flex items-center gap-2 bg-emergency-500 hover:bg-emergency-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-base disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {otpSubmitting ? 'Verifying…' : <>Verify & continue <ChevronRight className="h-4 w-4" /></>}
                </button>
              )}
              {step === 3 && (
                <button
                  type="button"
                  onClick={() => submitAddress()}
                  className="inline-flex items-center gap-2 bg-emergency-500 hover:bg-emergency-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-base"
                >
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              )}
              {step === 4 && (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => submitAdditionalInfo(true)}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => submitAdditionalInfo(false)}
                    className="inline-flex items-center gap-2 bg-emergency-500 hover:bg-emergency-600 disabled:opacity-60 text-white font-bold px-6 py-3 rounded-xl transition-colors text-base"
                  >
                    {submitting ? 'Submitting…' : <>Get help now <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {/* Close-form confirmation — same modal style as job cancel */}
      {confirmClose && createPortal(
        <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:justify-center sm:items-center sm:p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setConfirmClose(false)}
          />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm p-6 pb-8 sm:pb-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Close and lose progress?</h2>
              <button
                type="button"
                onClick={() => setConfirmClose(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Your answers will be lost. You can always start a new request if you still need a plumber.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmClose(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Keep going
              </button>
              <button
                type="button"
                onClick={() => {
                  clearSession()
                  setStep(0); setModalOpen(false); setConfirmClose(false)
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
              >
                Yes, close
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  )
}
